import { useEffect, useRef, useState, useCallback } from "react";
import { DatePicker, Button, Space, Typography, List, App, InputNumber } from "antd";
import { UploadOutlined, DownloadOutlined, AimOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import dayjs from "dayjs";
import CustomMap from "../../../components/maps/CustomMap";
import { VarietySelect } from "../../../components/common/VarietySelect";
import { parseKMLFile } from "../utils/kml-parser";
import { downloadKMLTemplate } from "../utils/kml-template";

interface PlotData {
  id?: number;
  geom: GeoJSON.Geometry;
  surface: number;
  estimated_planting_date?: string;
  poligono?: number | null;
  parcela?: number | null;
  recinto?: number | null;
  // PR3 — Variedad protegida declarada en contrato. Es la fuente de
  // verdad para la superficie licenciada por variedad de la finca.
  // Obligatoria en el form web; el backfill de PR2 dejó las parcelas
  // viejas con la `Farm.variety` actual.
  variety?: number | null;
}

export interface FarmInfo {
  name: string;
  province_name?: string;
  town_name?: string;
  surface_tango?: number;
  total_surface?: number;
  variety_display?: string;
  production_type?: string;
}

export interface MapWithDrawProps {
  initialPlots?: PlotData[];
  onPlotsChange: (plots: PlotData[]) => void;
  /**
   * Se dispara la PRIMERA vez que el usuario hace un cambio real
   * (draw, edit o delete) en el mapa. Permite al padre distinguir
   * entre "el mapa ha cargado con las parcelas iniciales sin más" y
   * "el usuario ha modificado activamente el conjunto". El padre lo
   * usa para no enviar `reference_plots` accidentalmente vacío en
   * un save inocente — el backend interpretaría esa lista vacía como
   * "borra todas las parcelas".
   */
  onDirty?: () => void;
  height?: string;
  defaultCenter?: [number, number];
  farmInfo?: FarmInfo;
}

type LayerKey = number;
interface PlotEntry { key: LayerKey; layer: L.Layer; data: PlotData; }

const PLOT_STYLE = {
  color: "#000000", weight: 3, fillOpacity: 0.3, fillColor: "#fa8c16",
};

const HIGHLIGHT_STYLE = {
  color: "#1677ff", weight: 4, fillOpacity: 0.5, fillColor: "#1677ff",
};

const computeSurfaceHa = (layer: L.Layer): number => {
  try {
    const latlngs = (layer as L.Polygon).getLatLngs();
    const ring = Array.isArray(latlngs[0]) && latlngs[0].length > 0 && Array.isArray(latlngs[0][0])
      ? (latlngs[0][0] as L.LatLng[]) : (latlngs[0] as L.LatLng[]);
    const areaM2 = L.GeometryUtil.geodesicArea(ring);
    return Math.round((areaM2 / 10000) * 10000) / 10000;
  } catch { return 0; }
};

const layerToGeom = (layer: L.Layer): GeoJSON.Geometry =>
  (layer as L.Polygon).toGeoJSON().geometry;

let keyCounter = 0;
const nextKey = (): LayerKey => ++keyCounter;

const buildFarmPopup = (info: FarmInfo): string => {
  const rows: string[] = [];
  rows.push(`<strong style="font-size:14px;">🏡 ${info.name}</strong>`);
  if (info.province_name || info.town_name)
    rows.push(`📍 ${[info.town_name, info.province_name].filter(Boolean).join(", ")}`);
  if (info.variety_display) rows.push(`🍊 Variedad: ${info.variety_display}`);
  if (info.surface_tango != null) rows.push(`📐 Sup. Tango: ${info.surface_tango.toFixed(2)} ha`);
  return `<div style="font-family:Arial,sans-serif;font-size:13px;line-height:1.6">${rows.join("<br/>")}</div>`;
};

const centerFromPlots = (plots: PlotData[]): [number, number] | undefined => {
  for (const p of plots) {
    if (!p.geom) continue;
    let coords: number[][] | undefined;
    if (p.geom.type === "Polygon") {
      coords = (p.geom as any).coordinates?.[0] as number[][] | undefined;
    } else if (p.geom.type === "MultiPolygon") {
      coords = (p.geom as any).coordinates?.[0]?.[0] as number[][] | undefined;
    }
    if (coords && coords.length > 0) {
      const avgLng = coords.reduce((s, c) => s + c[0], 0) / coords.length;
      const avgLat = coords.reduce((s, c) => s + c[1], 0) / coords.length;
      return [avgLat, avgLng];
    }
  }
  return undefined;
};

export const MapWithDraw: React.FC<MapWithDrawProps> = ({
  initialPlots,
  onPlotsChange,
  onDirty,
  height = "450px",
  defaultCenter,
  farmInfo,
}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const [plots, setPlots] = useState<PlotEntry[]>([]);
  const [highlightedKey, setHighlightedKey] = useState<LayerKey | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const featureGroupRef = useRef<L.FeatureGroup | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const drawControlRef = useRef<L.Control.Draw | null>(null);
  const initialLoadedRef = useRef(false);
  const kmlInputRef = useRef<HTMLInputElement | null>(null);
  // Dispara `onDirty` una única vez (idempotente) cuando el usuario
  // hace la primera modificación real. La carga de initialPlots NO
  // cuenta — solo los eventos draw/edit/delete disparados por el
  // usuario a través del control.
  const dirtyFiredRef = useRef(false);
  const onDirtyRef = useRef(onDirty);
  onDirtyRef.current = onDirty;
  const markDirty = useCallback(() => {
    if (dirtyFiredRef.current) return;
    dirtyFiredRef.current = true;
    onDirtyRef.current?.();
  }, []);

  const initialPlotsRef = useRef(initialPlots);
  initialPlotsRef.current = initialPlots;
  const farmInfoRef = useRef(farmInfo);
  farmInfoRef.current = farmInfo;
  const defaultCenterRef = useRef(defaultCenter);
  defaultCenterRef.current = defaultCenter;

  useEffect(() => {
    onPlotsChange(plots.map((p) => p.data));
  }, [plots]); // eslint-disable-line react-hooks/exhaustive-deps

  const computedCenter = (initialPlots && initialPlots.length > 0)
    ? centerFromPlots(initialPlots) ?? defaultCenter
    : defaultCenter;

  const handleMapReady = useCallback((map: L.Map) => {
    // Always accept the new map (React StrictMode destroys and recreates)
    mapRef.current = map;
    featureGroupRef.current = null;
    drawControlRef.current = null;
    initialLoadedRef.current = false;

    const featureGroup = new L.FeatureGroup();
    featureGroupRef.current = featureGroup;
    map.addLayer(featureGroup);

    const drawControl = new L.Control.Draw({
      position: "topleft",
      edit: { featureGroup },
      draw: {
        polygon: { allowIntersection: false, showArea: false, shapeOptions: { ...PLOT_STYLE } },
        polyline: false, rectangle: false, circle: false, marker: false, circlemarker: false,
      },
    });
    drawControlRef.current = drawControl;
    map.addControl(drawControl);

    map.on(L.Draw.Event.CREATED, (event: any) => {
      const layer = event.layer as L.Polygon;
      featureGroup.addLayer(layer);
      setPlots((prev) => [...prev, {
        key: nextKey(), layer,
        data: { geom: layerToGeom(layer), surface: computeSurfaceHa(layer) },
      }]);
      markDirty();
      message.success(t("producers.map.draw_polygon", "Polígono dibujado"));
    });

    map.on(L.Draw.Event.EDITED, (event: any) => {
      const editedLayers = event.layers as L.LayerGroup;
      setPlots((prev) => {
        const updated = [...prev];
        editedLayers.eachLayer((el: L.Layer) => {
          const idx = updated.findIndex((p) => p.layer === el);
          if (idx !== -1) {
            updated[idx] = { ...updated[idx], data: { ...updated[idx].data, geom: layerToGeom(el), surface: computeSurfaceHa(el) } };
          }
        });
        return updated;
      });
      markDirty();
    });

    map.on(L.Draw.Event.DELETED, (event: any) => {
      const deletedLayers = event.layers as L.LayerGroup;
      setPlots((prev) => {
        const deletedSet = new Set<L.Layer>();
        deletedLayers.eachLayer((l: L.Layer) => deletedSet.add(l));
        return prev.filter((p) => !deletedSet.has(p.layer));
      });
      markDirty();
    });

    // ═══════════════════════════════════════════════════════════
    // Load initial plots from props (via refs)
    // ═══════════════════════════════════════════════════════════
    const plotsToLoad = initialPlotsRef.current;
    const info = farmInfoRef.current;
    const fallbackCenter = defaultCenterRef.current;

    if (plotsToLoad && plotsToLoad.length > 0) {
      initialLoadedRef.current = true;
      const entries: PlotEntry[] = [];

      plotsToLoad.forEach((plot) => {
        try {
          const feature: GeoJSON.Feature = { type: "Feature", properties: {}, geometry: plot.geom };
          const geoLayer = L.geoJSON(feature, { style: { ...PLOT_STYLE } });
          geoLayer.eachLayer((layer) => {
            featureGroup.addLayer(layer);
            entries.push({ key: nextKey(), layer, data: { ...plot } });
          });
        } catch (err) {
          console.warn("[MapWithDraw] Failed to load plot:", plot.id, err);
        }
      });

      setPlots(entries);

      // Fit bounds + farm info marker
      requestAnimationFrame(() => {
        try {
          const bounds = featureGroup.getBounds();
          if (bounds.isValid()) {
            map.invalidateSize();
            map.fitBounds(bounds, { padding: [40, 40], maxZoom: 17 });
            if (info) {
              const center = bounds.getCenter();
              L.marker(center).addTo(map).bindPopup(buildFarmPopup(info));
            }
          } else if (fallbackCenter) {
            map.setView(fallbackCenter, 12);
          }
        } catch (err) {
          console.warn("[MapWithDraw] fitBounds error:", err);
          if (fallbackCenter) map.setView(fallbackCenter, 12);
        }
      });
    } else if (fallbackCenter) {
      map.setView(fallbackCenter, 12);
    }

  }, [t, message, markDirty]);

  const processKMLFile = useCallback(async (file: File) => {
    try {
      const geojson = await parseKMLFile(file);
      const featureGroup = featureGroupRef.current;
      const map = mapRef.current;
      if (!featureGroup || !map) return;
      const newEntries: PlotEntry[] = [];
      geojson.features.forEach((feature) => {
        const geoLayer = L.geoJSON(feature as any, { style: { ...PLOT_STYLE } });
        geoLayer.eachLayer((layer) => {
          featureGroup.addLayer(layer);
          newEntries.push({ key: nextKey(), layer, data: { geom: layerToGeom(layer), surface: computeSurfaceHa(layer) } });
        });
      });
      setPlots((prev) => [...prev, ...newEntries]);
      const bounds = featureGroup.getBounds();
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40] });
      // KML upload = user acción → dirty explícito.
      markDirty();
      message.success(t("producers.map.kml_loaded", "KML cargado correctamente"));
    } catch (err: any) {
      message.error(err?.message || t("producers.map.invalid_kml", "El archivo KML no es válido"));
    }
  }, [t, message, markDirty]);

  const handleKMLUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (kmlInputRef.current) kmlInputRef.current.value = "";
    if (!file) return;
    await processKMLFile(file);
  }, [processKMLFile]);

  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes("Files")) setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".kml")) {
      message.error(t("producers.map.invalid_kml_ext", "Solo se aceptan archivos .kml"));
      return;
    }
    await processKMLFile(file);
  }, [processKMLFile, message, t]);

  const handleDateChange = (key: LayerKey, year: number | undefined) => {
    const dateStr = year ? `${year}-01-01` : undefined;
    setPlots((prev) => prev.map((p) => (p.key === key ? { ...p, data: { ...p.data, estimated_planting_date: dateStr } } : p)));
    markDirty();
  };

  const handleFieldChange = (key: LayerKey, field: keyof PlotData, value: any) => {
    setPlots((prev) => prev.map((p) => (p.key === key ? { ...p, data: { ...p.data, [field]: value } } : p)));
    markDirty();
  };

  const handleFocusPlot = (entry: PlotEntry) => {
    const map = mapRef.current;
    if (!map) return;

    // Reset previous highlight
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    plots.forEach((p) => {
      if ((p.layer as L.Polygon).setStyle) (p.layer as L.Polygon).setStyle(PLOT_STYLE);
    });

    // Highlight selected
    if ((entry.layer as L.Polygon).setStyle) {
      (entry.layer as L.Polygon).setStyle(HIGHLIGHT_STYLE);
    }
    setHighlightedKey(entry.key);

    // Zoom to bounds
    try {
      const bounds = (entry.layer as L.Polygon).getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [60, 60], maxZoom: 18 });
      }
    } catch { /* ignore */ }

    // Revert highlight after 3s
    highlightTimerRef.current = setTimeout(() => {
      if ((entry.layer as L.Polygon).setStyle) (entry.layer as L.Polygon).setStyle(PLOT_STYLE);
      setHighlightedKey(null);
    }, 3000);
  };

  return (
    <div>
      <CustomMap
        height={height}
        initialCenter={computedCenter}
        initialZoom={computedCenter ? 14 : undefined}
        onMapReady={handleMapReady}
        showSearchBar={false}
        showAddressSearch={true}
        showLayerControl={true}
      />
      <input ref={kmlInputRef} type="file" accept=".kml" style={{ display: "none" }} onChange={handleKMLUpload} />
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{
          marginTop: 8,
          marginBottom: 8,
          padding: isDragging ? "12px 16px" : "4px 0",
          border: isDragging ? "2px dashed #1677ff" : "2px dashed transparent",
          borderRadius: 8,
          backgroundColor: isDragging ? "#e6f4ff" : "transparent",
          transition: "all 0.2s ease",
        }}
      >
        {isDragging ? (
          <div style={{ textAlign: "center", color: "#1677ff", fontWeight: 500 }}>
            <UploadOutlined style={{ fontSize: 20, marginRight: 8 }} />
            {t("producers.map.drop_kml", "Suelta el archivo KML aquí")}
          </div>
        ) : (
          <Space>
            <Button icon={<UploadOutlined />} onClick={() => kmlInputRef.current?.click()}>
              {t("producers.map.upload_kml", "Cargar KML")}
            </Button>
            <Button icon={<DownloadOutlined />} onClick={downloadKMLTemplate}>
              {t("producers.map.download_template", "Descargar plantilla KML")}
            </Button>
          </Space>
        )}
      </div>
      {plots.length > 0 && (
        <List
          size="small"
          dataSource={plots}
          renderItem={(entry, index) => (
            <List.Item>
              <Space wrap>
                <Button
                  type={highlightedKey === entry.key ? "primary" : "default"}
                  size="small"
                  icon={<AimOutlined />}
                  onClick={() => handleFocusPlot(entry)}
                  title={t("producers.map.focus_plot", "Centrar en parcela")}
                />
                <Typography.Text>
                  {`${t("producers.farms.reference_plots", "Parcela")} ${index + 1}`}
                </Typography.Text>
                <InputNumber
                  addonAfter="ha"
                  placeholder={t("producers.map.surface", "Sup.")}
                  value={entry.data.surface}
                  onChange={(val) =>
                    handleFieldChange(entry.key, "surface", val ?? 0)
                  }
                  style={{ width: 140 }}
                  min={0}
                  step={0.01}
                  decimalSeparator=","
                  precision={4}
                  title={t(
                    "producers.map.surface_tooltip",
                    "Superficie de la parcela. Se autocalcula al dibujar/editar la geometría; edítala si el polígono no representa la superficie real.",
                  )}
                />
                <InputNumber
                  placeholder={t("producers.map.poligono", "Polígono")}
                  value={entry.data.poligono}
                  onChange={(val) => handleFieldChange(entry.key, "poligono", val)}
                  style={{ width: 110 }}
                  min={0}
                />
                <InputNumber
                  placeholder={t("producers.map.parcela", "Parcela")}
                  value={entry.data.parcela}
                  onChange={(val) => handleFieldChange(entry.key, "parcela", val)}
                  style={{ width: 110 }}
                  min={0}
                />
                <InputNumber
                  placeholder={t("producers.map.recinto", "Recinto")}
                  value={entry.data.recinto}
                  onChange={(val) => handleFieldChange(entry.key, "recinto", val)}
                  style={{ width: 110 }}
                  min={0}
                />
                <DatePicker
                  picker="year"
                  placeholder={t("producers.map.estimated_planting_year", "Año plantación")}
                  value={entry.data.estimated_planting_date ? dayjs(entry.data.estimated_planting_date) : undefined}
                  onChange={(date) => handleDateChange(entry.key, date ? date.year() : undefined)}
                  style={{ width: 120 }}
                />
                {/* PR3 — Variedad protegida obligatoria por parcela.
                    El form padre puede pasar `includeInactive` si
                    está editando una parcela con una variedad
                    archivada (no nos lo planteamos aquí — el
                    selector estándar es suficiente para crear). */}
                <VarietySelect
                  placeholder={t("producers.map.variety", "Variedad")}
                  value={entry.data.variety ?? undefined}
                  onChange={(val) => handleFieldChange(entry.key, "variety", val ?? null)}
                  allowClear
                  style={{ width: 180 }}
                  status={
                    entry.data.variety == null ? "warning" : undefined
                  }
                />
              </Space>
            </List.Item>
          )}
        />
      )}
    </div>
  );
};
